import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { IFrame } from 'Components/atoms/IFrame';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * Convertr Form component
 * @param {string} headingLabel - Text of the heading
 * @param {number} headingSize - size of the heading
 * @param {string} title - title of the podcast
 * @param {number} titleSize - heading tag size for title
 * @param {string} excerpt - excerpt of the podcast
 * @param {string} podcastUrl - to embed the podcast iframe
 * @param {string} viewMoreLabel -  label for the view more link
 * @param {string} viewMorePosition -  where to have the view all link
 * @param {string} viewMoreUrl - url for the view more link
 * @param {string} viewMoreIcon - icon title to accompany view more link
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */

const Podcast = (
  {
    headingLabel,
    headingSize,
    title,
    titleSize,
    excerpt,
    podcastUrl,
    viewMoreLabel,
    viewMoreUrl,
    viewMoreIcon,
    viewMorePosition,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => (
  <div
    className={classNames({
      [`${cssPrefix}__podcast`]: loadStyles,
      ...extraClassNames,
    })}
  >
    <div className={`${cssPrefix}__podcast--main`}>
      {headingLabel && (
        <div className={`${cssPrefix}__podcast--heading-container`}>
          <div className={`${cssPrefix}__podcast--block`}>
            <Heading
              size={headingSize}
              extraClassNames={{ [`${cssPrefix}__podcast--heading`]: true }}
            >
              {headingLabel}
            </Heading>
          </div>
        </div>
      )}

      {title && (
        <div className={`${cssPrefix}__podcast--header`}>
          <Heading
            size={titleSize}
            extraClassNames={{ [`${cssPrefix}__podcast--title`]: true }}
          >
            {title}
          </Heading>
        </div>
      )}

      {excerpt && (
        <div className={`${cssPrefix}__podcast--content`}>
          <p className={`${cssPrefix}__podcast--excerpt`}>{excerpt}</p>
        </div>
      )}

      {viewMorePosition === 'after-excerpt' && viewMoreLabel && viewMoreUrl && (
        <div className={`${cssPrefix}__podcast--view-more -${viewMorePosition}`}>
          <Link href={viewMoreUrl}>
            {viewMoreLabel}
            {viewMoreIcon && (
              <Icon icon={`${cssPrefix}__${viewMoreIcon}`} />
            )}
          </Link>
        </div>
      )}
    </div>

    <div className={`${cssPrefix}__podcast--media`}>
      {podcastUrl && (
        <IFrame
          iframeUrl={podcastUrl}
          extraClassNames={{ [`${cssPrefix}__podcast--iframe`]: true }}
        />
      )}
    </div>

    {viewMorePosition === 'footer' && viewMoreLabel && viewMoreUrl && (
      <div className={`${cssPrefix}__podcast--footer`}>
        <div className={`${cssPrefix}__podcast--view-more -${viewMorePosition}`}>
          <Link href={viewMoreUrl}>
            {viewMoreLabel}
            {viewMoreIcon && (
              <Icon icon={`${cssPrefix}__${viewMoreIcon}`} />
            )}
          </Link>
        </div>
      </div>
    )}
  </div>
);

/**
 * Valid props
 */
Podcast.propTypes = {
  headingLabel: PropTypes.string,
  headingSize: PropTypes.number,
  title: PropTypes.string,
  titleSize: PropTypes.number,
  excerpt: PropTypes.string,
  podcastUrl: PropTypes.string,
  viewMoreLabel: PropTypes.string,
  viewMoreUrl: PropTypes.string,
  viewMoreIcon: PropTypes.string,
  viewMorePosition: PropTypes.string,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
};

/**
 * Default props
 */
Podcast.defaultProps = {
  headingLabel: '',
  headingSize: 2,
  title: '',
  titleSize: 3,
  excerpt: '',
  podcastUrl: '',
  viewMoreLabel: '',
  viewMoreUrl: '',
  viewMoreIcon: '',
  viewMorePosition: 'footer',
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(Podcast);
